import { useState, useEffect } from 'react';
import { Modal, Tab, Tabs, Table, Button, Badge } from 'react-bootstrap';
import { AdminService } from '@/services/admin.service';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ChatPanel } from '@/components/chat/ChatPanel';

export function AdminPage() {
    const [key, setKey] = useState('overview');
    const [users, setUsers] = useState<any[]>([]);
    const [playlists, setPlaylists] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showChatPlaylistId, setShowChatPlaylistId] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersData, playlistsData] = await Promise.all([
                AdminService.getAllUsers(),
                AdminService.getAllPlaylists()
            ]);
            setUsers(usersData);
            setPlaylists(playlistsData);
        } catch (error) {
            console.error('Failed to fetch admin data', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleRoleUpdate = async (userId: string, newRole: 'admin' | 'user') => {
        if (!confirm(`Are you sure you want to make this user a ${newRole}?`)) return;
        try {
            await AdminService.updateUserRole(userId, newRole);
            fetchData(); // Refresh
        } catch (error) {
            console.error('Failed to update role', error);
        }
    };

    const handleDeleteUser = async (userId: string) => {
        if (!confirm('Are you sure? This will delete the user profile (Auth deletion requires backend).')) return;
        try {
            await AdminService.deleteUser(userId);
            fetchData();
        } catch (error) {
            console.error('Failed to delete user', error);
        }
    };

    const handleDeletePlaylist = async (playlistId: string) => {
        if (!confirm('Are you sure you want to delete this playlist?')) return;
        try {
            await AdminService.deletePlaylist(playlistId);
            fetchData();
        } catch (error) {
            console.error('Failed to delete playlist', error);
        }
    };

    if (loading) return <LoadingSpinner fullScreen />;

    // Calc Stats
    const stats = {
        totalUsers: users.length,
        totalPlaylists: playlists.length,
        totalTracks: playlists.reduce((acc, p) => acc + (p.tracks?.length || 0), 0),
        activeSessions: 0 // Need session service for this, skip for MVP
    };

    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Admin Dashboard</h1>
                <Badge bg="danger">Admin Access</Badge>
            </div>

            <Tabs
                id="admin-tabs"
                activeKey={key}
                onSelect={(k) => setKey(k || 'overview')}
                className="mb-4"
            >
                <Tab eventKey="overview" title="Overview">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="card text-center p-3 shadow-sm">
                                <h3 className="display-4 fw-bold">{stats.totalUsers}</h3>
                                <p className="text-muted mb-0">Total Users</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card text-center p-3 shadow-sm">
                                <h3 className="display-4 fw-bold">{stats.totalPlaylists}</h3>
                                <p className="text-muted mb-0">Total Playlists</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card text-center p-3 shadow-sm">
                                <h3 className="display-4 fw-bold">{stats.totalTracks}</h3>
                                <p className="text-muted mb-0">Total Tracks</p>
                            </div>
                        </div>
                    </div>
                </Tab>

                <Tab eventKey="users" title="Users">
                    <div className="card shadow-sm">
                        <div className="card-header bg-body-secondary d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">User Management</h5>
                            <Button variant="outline-secondary" size="sm" onClick={fetchData}>Refresh</Button>
                        </div>
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="rounded-circle bg-secondary d-flex justify-content-center align-items-center text-white" style={{ width: 32, height: 32 }}>
                                                    {u.name?.charAt(0) || u.email?.charAt(0) || '?'}
                                                </div>
                                                {u.name || 'No Name'}
                                            </div>
                                        </td>
                                        <td>{u.email}</td>
                                        <td>
                                            <Badge bg={u.role === 'admin' ? 'danger' : 'primary'}>
                                                {u.role || 'user'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {u.role !== 'admin' ? (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleRoleUpdate(u.id, 'admin')}
                                                >
                                                    Make Admin
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleRoleUpdate(u.id, 'user')}
                                                >
                                                    Remove Admin
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteUser(u.id)}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Tab>

                <Tab eventKey="playlists" title="Playlists">
                    <div className="card shadow-sm">
                        <div className="card-header bg-body-secondary">
                            <h5 className="mb-0">All Playlists</h5>
                        </div>
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Playlist</th>
                                    <th>Tracks</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {playlists.map(p => (
                                    <tr key={p.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="bg-secondary rounded" style={{ width: 40, height: 40 }}></div>
                                                {p.name}
                                            </div>
                                        </td>
                                        <td>{p.tracks?.length || 0}</td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => setShowChatPlaylistId(p.id)}
                                            >
                                                Chat
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeletePlaylist(p.id)}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Tab>

            </Tabs>

            {/* Chat Modal */}
            <Modal show={!!showChatPlaylistId} onHide={() => setShowChatPlaylistId(null)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Chat Management</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ height: '70vh' }}>
                    {showChatPlaylistId && <ChatPanel playlistId={showChatPlaylistId} />}
                </Modal.Body>
            </Modal>
        </div >
    );
}
